import axios, { AxiosError } from 'axios';
import { GameName, STORAGE_KEYS } from '../constants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// API client configuration
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Debug logging
  if (config.url?.includes('create-room')) {
    console.log('=== CREATE ROOM REQUEST DEBUG ===');
    console.log('URL:', config.url);
    console.log('Method:', config.method);
    console.log('Data:', config.data);
    console.log('================================');
  }
  
  return config;
});

// Error handler
const handleApiError = (error: AxiosError): string => {
  console.log('=== API ERROR DEBUG ===');
  console.log('Status:', error.response?.status);
  console.log('Data:', error.response?.data);
  console.log('=====================');
  
  if (error.response?.data) {
    const data = error.response.data as any;
    
    // Handle validation errors (array of error objects)
    if (Array.isArray(data.detail)) {
      return data.detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
    }
    
    // Handle string detail or error
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    return data.error || 'An error occurred';
  }
  return error.message || 'Network error';
};

// API functions
export const api = {
  // Authentication
  login: async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/api/login', { email, password });
      const { token, user } = response.data;
      
      console.log('=== LOGIN RESPONSE DEBUG ===');
      console.log('Token:', token);
      console.log('User:', user);
      console.log('===========================');
      
      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, 'user');
      localStorage.setItem(STORAGE_KEYS.USERNAME, user.userAlias);
      
      console.log('=== STORED IN LOCALSTORAGE ===');
      console.log('USER_DATA:', localStorage.getItem(STORAGE_KEYS.USER_DATA));
      console.log('==============================');
      
      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  signup: async (email: string, password: string, passwordConfirm: string, alias: string) => {
    try {
      const response = await apiClient.post('/api/signup', {
        email,
        password,
        userAlias: alias,
      });
      const { token, user } = response.data;
      
      console.log('=== SIGNUP RESPONSE DEBUG ===');
      console.log('Token:', token);
      console.log('User:', user);
      console.log('============================');
      
      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, 'user');
      localStorage.setItem(STORAGE_KEYS.USERNAME, user.userAlias);
      
      console.log('=== STORED IN LOCALSTORAGE ===');
      console.log('USER_DATA:', localStorage.getItem(STORAGE_KEYS.USER_DATA));
      console.log('==============================');
      
      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  googleAuth: async (token: string, email: string, name: string, googleId: string) => {
    try {
      const response = await apiClient.post('/api/google-auth', {
        token,
        email,
        name,
      });
      const { token: jwtToken, user } = response.data;
      
      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, jwtToken);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, 'user');
      localStorage.setItem(STORAGE_KEYS.USERNAME, user.userAlias);
      
      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  verifyToken: async () => {
    try {
      const response = await apiClient.get('/api/verify-token');
      return { success: true, data: response.data };
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    localStorage.removeItem(STORAGE_KEYS.USERNAME);
  },

  // Room management
  createRoom: async (gameType: string, roomName: string, maxPlayers: number, adminName?: string, minPlayers: number = 2, gameConfig?: any) => {
    try {
      const response = await apiClient.post('/api/create-room', {
        roomName,
        gameType,
        maxParticipants: maxPlayers,
        minParticipants: minPlayers,
        gameConfig: gameConfig || {},
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  joinGame: async (roomId: string, userAlias: string) => {
    try {
      const response = await apiClient.post(`/api/join-game/${roomId}`, {
        userAlias,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  startGame: async (roomId: string) => {
    try {
      const response = await apiClient.post(`/api/start-game/${roomId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  endGame: async (roomId: string) => {
    try {
      const response = await apiClient.post(`/api/end-game/${roomId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  deleteRoom: async (roomId: string) => {
    try {
      const response = await apiClient.delete(`/api/delete-room/${roomId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  getScore: async (roomId: string, userId: string) => {
    try {
      const response = await apiClient.get(`/api/score/${roomId}/${userId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  updateScore: async (roomId: string, userId: string, updates: any) => {
    try {
      const response = await apiClient.patch(`/api/score/${roomId}/${userId}`, {
        updates,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  getLeaderboard: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/leaderboard/${roomId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  getRoom: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/room/${roomId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  // Guess The Person
  submitAnswers: async (roomId: string, answers: Record<string, string>) => {
    try {
      const response = await apiClient.post(`/api/guess-the-person/${roomId}/submit-answers`, { answers });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  getAllAnswers: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/guess-the-person/${roomId}/answers`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  getSubmissionStatus: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/guess-the-person/${roomId}/submission-status`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  // Guest authentication (no account required)
  guestLogin: async (userAlias: string) => {
    try {
      const response = await apiClient.post('/api/guest-login', { userAlias });
      const { token, user } = response.data;
      localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, 'guest');
      localStorage.setItem(STORAGE_KEYS.USERNAME, user.userAlias);
      return { success: true, data: user };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  // Gender Equality Cuisine
  gecSetRole: async (roomId: string, role: 'chef' | 'voter') => {
    try {
      const response = await apiClient.post(`/api/gender-equality-cuisine/${roomId}/set-role`, { role });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecSubmitDish: async (
    roomId: string,
    dish: { dishName: string; realName: string; allergy: string; identifyingDetails: string; description: string }
  ) => {
    try {
      const response = await apiClient.post(`/api/gender-equality-cuisine/${roomId}/submit-dish`, dish);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecGetRoomStatus: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/gender-equality-cuisine/${roomId}/room-status`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecGetDishes: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/gender-equality-cuisine/${roomId}/dishes`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecGetMyVote: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/gender-equality-cuisine/${roomId}/my-vote`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecSubmitVotes: async (roomId: string, votes: Record<string, number>) => {
    try {
      const response = await apiClient.post(`/api/gender-equality-cuisine/${roomId}/submit-votes`, { votes });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },

  gecGetResults: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/api/gender-equality-cuisine/${roomId}/results`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error as AxiosError) };
    }
  },
};

export default api;
